"use client";

import { useSyncExternalStore } from "react";
import { getPublicEnv } from "@janeway/app-sdk/public-env";
import { JANE_URSPRUENGE, janeAktiv, janeLokalOptIn, umgebungVonChatUrsprung } from "@janeway/app-sdk/jane";

import { WECKWORT_HINWEIS_VERSION } from "../lib/weckwort-hinweis";

/**
 * „Hey Jane“ auf der Wirt-Seite (Weckwort-Spec §5): Einwilligung am Konto, Schalter je Geraet.
 *
 * - **Konto:** `user_preferences.jane_weckwort` (M1079) — der Nachweis, dass erklaert und gefragt
 *   wurde. Ohne gueltige Einwilligung schaltet niemand das Geraet ein; zuerst kommt der Erklaertext.
 * - **Geraet:** Keks `jw-weckwort` = `"an"` | `"aus"` auf der Eltern-Domain (`NEXT_PUBLIC_COOKIE_DOMAIN`),
 *   dazu `localStorage["jw-weckwort"]` als Rueckfall bei gesperrten Keksen. Der Keks zuerst: localStorage
 *   ist je Subdomain, und eine App, in der nie geschaltet wurde, montierte den Knoten sonst nur im Leerlauf
 *   — den es am Handy nicht gibt, dort lauschte „Hey Jane“ in dieser App nie. localStorage ist je URSPRUNG: der
 *   Chat (der allein lauscht) haelt seinen eigenen Eintrag unter demselben Schluessel. Hier ist er der
 *   Hinweis fuer die Huelle, den Jane-Knoten gleich beim Laden zu montieren. Eine Entscheidung der
 *   Person geht deshalb zusaetzlich als `weckwort_schalten` an den Chat (`ausstehendesWeckwortSchalten`,
 *   gesendet nach `hallo`); was der Chat als `lauscht: true` meldet, spiegelt der Wirt nur in seinen
 *   Hinweis zurueck und schickt es nie wieder hin — ein veralteter Hinweis dieses Ursprungs schaltet
 *   den Chat so nie wieder ein, den die Person in einer anderen App ausgeschaltet hat.
 */

export const WECKWORT_GERAET_SCHLUESSEL = "jw-weckwort";

export interface JaneWeckwortEinwilligung {
  eingewilligt_am?: string;
  hinweis_version: number;
  hinweis_gesehen_am?: string;
}

function istEinwilligung(v: unknown): v is JaneWeckwortEinwilligung {
  if (!v || typeof v !== "object" || Array.isArray(v)) return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.hinweis_version === "number" &&
    (o.eingewilligt_am === undefined || typeof o.eingewilligt_am === "string") &&
    (o.hinweis_gesehen_am === undefined || typeof o.hinweis_gesehen_am === "string")
  );
}

/** Eine Einwilligung zaehlt nur fuer den Text, der jetzt gilt (Version zaehlt hoch, Spec §5). */
export function einwilligungGilt(e: Partial<JaneWeckwortEinwilligung> | null | undefined): boolean {
  return !!e?.eingewilligt_am && (e.hinweis_version ?? 0) >= WECKWORT_HINWEIS_VERSION;
}

/**
 * Bietet dieser Ursprung „Hey Jane“ an? Ein Jane-Wirt (`janeAktiv`), der Chat selbst und die
 * Anmeldung derselben Umgebung (`auth.` neben dem Chat-Ursprung — dort laeuft die Registrierung,
 * und dort ist `janeAktiv` falsch, weil die Anmeldung kein Wirt ist). Prod: nie, solange es dort
 * keinen Chat-Ursprung gibt. Kein Sicherheitsurteil, nur eine Anzeigefrage.
 */
export function weckwortImUmfeld(ursprung: string): boolean {
  if (janeAktiv(ursprung, { lokalOptIn: janeLokalOptIn() })) return true;
  if (umgebungVonChatUrsprung(ursprung) !== null) return true;
  const chat = JANE_URSPRUENGE.labs.chat;
  return chat !== null && ursprung === chat.replace("://chat.", "://auth.");
}

// ── Geraet ─────────────────────────────────────────────────────────────────────

const hoerer = new Set<() => void>();
let ausstehend: boolean | null = null;

function melde() {
  for (const h of hoerer) h();
}

function abonniere(h: () => void): () => void {
  hoerer.add(h);
  const beiSpeicher = (e: StorageEvent) => {
    if (e.key === WECKWORT_GERAET_SCHLUESSEL) h();
  };
  window.addEventListener("storage", beiSpeicher);
  return () => {
    hoerer.delete(h);
    window.removeEventListener("storage", beiSpeicher);
  };
}

/** Ein Jahr: der Schalter ist eine Geraete-Einstellung, kein Sitzungszustand. */
const KEKS_MAX_AGE = 60 * 60 * 24 * 365;

function leseKeks(): "an" | "aus" | null {
  try {
    const treffer = document.cookie.match(new RegExp(`(?:^|;\\s*)${WECKWORT_GERAET_SCHLUESSEL}=(an|aus)(?:;|$)`));
    return treffer ? (treffer[1] as "an" | "aus") : null;
  } catch {
    return null;
  }
}

function schreibeKeks(an: boolean) {
  try {
    const domain = getPublicEnv("NEXT_PUBLIC_COOKIE_DOMAIN");
    // Ein host-only Doppelgaenger gleichen Namens ueberschattete den geteilten beim Lesen
    // (wie beim Install-Merkzettel); `Max-Age=0` OHNE `Domain` trifft nur ihn.
    if (domain) document.cookie = `${WECKWORT_GERAET_SCHLUESSEL}=; Path=/; Max-Age=0`;
    const teile = [`${WECKWORT_GERAET_SCHLUESSEL}=${an ? "an" : "aus"}`, "Path=/", `Max-Age=${KEKS_MAX_AGE}`, "SameSite=Lax"];
    if (domain) teile.push(`Domain=${domain}`);
    if (window.location.protocol === "https:") teile.push("Secure");
    document.cookie = teile.join("; ");
  } catch {
    // Gesperrte Kekse: dann gilt localStorage dieses Ursprungs.
  }
}

export function leseWeckwortGeraet(): boolean {
  const keks = leseKeks();
  if (keks !== null) return keks === "an";
  try {
    return window.localStorage.getItem(WECKWORT_GERAET_SCHLUESSEL) === "an";
  } catch {
    return false;
  }
}

function schreibeGeraet(an: boolean) {
  schreibeKeks(an);
  try {
    window.localStorage.setItem(WECKWORT_GERAET_SCHLUESSEL, an ? "an" : "aus");
  } catch {
    // Gesperrter Speicher: gilt dann nur fuer dieses Dokument (ausstehendes Schalten).
  }
}

/** Die Person schaltet „Hey Jane“ an diesem Geraet ein/aus. Geht nach `hallo` an den Chat. */
export function schalteWeckwort(an: boolean): void {
  schreibeGeraet(an);
  ausstehend = an;
  melde();
}

/** Der Chat meldet, dass er lauscht — nur den Hinweis dieses Ursprungs nachziehen, nichts senden. */
export function spiegleWeckwortGeraet(an: boolean): void {
  // Immer melden: der Keks ist ueber die Eltern-Domain geteilt, im Jane-Rahmen hat ihn der Wirt oft schon
  // geschrieben. Eine Abkuerzung „steht schon so“ liess die Hoerer dort ohne Nachricht — das Mikrofon lief nach
  // dem Ausschalten weiter (Abnahme 2026-10-17, calendar).
  if (leseWeckwortGeraet() !== an) schreibeGeraet(an);
  melde();
}

export function ausstehendesWeckwortSchalten(): boolean | null {
  return ausstehend;
}

/** Holt die wartende Entscheidung ab (der Knoten hat sie gesendet). */
export function nimmAusstehendesWeckwortSchalten(): boolean | null {
  const a = ausstehend;
  ausstehend = null;
  if (a !== null) melde();
  return a;
}

/** Geraet an? SSR und vor der Hydration: aus. */
export function useWeckwortGeraet(): boolean {
  return useSyncExternalStore(abonniere, leseWeckwortGeraet, () => false);
}

/** Wartende Entscheidung der Person fuer den Chat, oder `null`. */
export function useAusstehendesWeckwortSchalten(): boolean | null {
  return useSyncExternalStore(abonniere, ausstehendesWeckwortSchalten, () => null);
}

// ── Konto ──────────────────────────────────────────────────────────────────────

async function konto() {
  const { createSupabaseBrowser } = await import("@janeway/app-sdk/supabase");
  const supabase = createSupabaseBrowser();
  const {
    data: { session },
  } = await supabase.auth.getSession();
  return session ? { supabase, nutzer: session.user.id } : null;
}

/** `jane_weckwort` des angemeldeten Kontos, oder `null` (keine Zeile, keine Sitzung, fremde Form). */
export async function leseWeckwortEinwilligung(): Promise<JaneWeckwortEinwilligung | null> {
  try {
    const k = await konto();
    if (!k) return null;
    const { data } = await k.supabase
      .from("user_preferences")
      .select("jane_weckwort")
      .eq("user_id", k.nutzer)
      .maybeSingle();
    const roh = (data as { jane_weckwort?: unknown } | null)?.jane_weckwort;
    return istEinwilligung(roh) ? roh : null;
  } catch {
    return null;
  }
}

async function schreibeEinwilligung(aendern: (alt: JaneWeckwortEinwilligung | null) => JaneWeckwortEinwilligung) {
  try {
    const k = await konto();
    if (!k) return false;
    const alt = await leseWeckwortEinwilligung();
    const { error } = await k.supabase.from("user_preferences").upsert(
      { user_id: k.nutzer, jane_weckwort: aendern(alt), updated_at: new Date().toISOString() },
      { onConflict: "user_id" },
    );
    return !error;
  } catch {
    return false;
  }
}

/** Einwilligung mit dem aktuellen Erklaertext festhalten (Art. 7 Abs. 1). `true` = gespeichert. */
export function willigeWeckwortEin(): Promise<boolean> {
  return schreibeEinwilligung((alt) => ({
    ...(alt?.hinweis_gesehen_am ? { hinweis_gesehen_am: alt.hinweis_gesehen_am } : {}),
    eingewilligt_am: new Date().toISOString(),
    hinweis_version: WECKWORT_HINWEIS_VERSION,
  }));
}

/**
 * Einmal-Hinweis fuer Bestandsnutzer gesehen (Spec §5, F6; gezeigt im Jane-Panel von ai-chat).
 * Behaelt eine bestehende Einwilligung; ohne eine bleibt `eingewilligt_am` weg.
 */
export function merkeWeckwortHinweisGesehen(): Promise<boolean> {
  return schreibeEinwilligung((alt) => ({
    ...(alt ?? {}),
    hinweis_version: alt?.eingewilligt_am ? alt.hinweis_version : WECKWORT_HINWEIS_VERSION,
    hinweis_gesehen_am: new Date().toISOString(),
  }));
}
